import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconDots,
  IconEdit,
  IconPlus,
  IconRestore,
  IconTrash,
  IconUnlink,
  IconVideoPlus,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { markOwnProgrammeWrite, qk } from '@/api/queries';
import type {
  AdminEpisode,
  AdminProgramme,
  AdminSeason,
  AdminVideo,
  EpisodePatch,
} from '@/api/types';
import { toDate, toIso } from '@/lib/format';
import { EmptyState } from '@/components/EmptyState';
import { VideoPickerModal } from '@/features/videos/VideoPickerModal';
import { VideoSummary } from '@/features/videos/VideoSummary';
import { Section } from './DetailsTab';

/** Season/episode/video edits save immediately (they're separate records from the programme form). */
function useEpisodeMutation<TArgs>(
  programmeId: string,
  fn: (a: TArgs) => Promise<unknown>,
  success?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      markOwnProgrammeWrite(programmeId);
      void qc.invalidateQueries({ queryKey: qk.programme(programmeId) });
      void qc.invalidateQueries({ queryKey: ['programmes'] });
      void qc.invalidateQueries({ queryKey: ['videos'] });
      if (success) notifications.show({ color: 'leaf', message: success });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not saved', message: errorMessage(e) }),
  });
}

export function EpisodesTab({ programme }: { programme: AdminProgramme }) {
  const [seasonModal, setSeasonModal] = useState<AdminSeason | 'new' | null>(null);
  const [episodeModal, setEpisodeModal] = useState<{
    episode?: AdminEpisode;
    seasonId?: string | null;
  } | null>(null);
  const deleteSeason = useEpisodeMutation(
    programme.id,
    (id: string) => adminApi.deleteSeason(id),
    'Series removed.',
  );

  if (programme.kind === 'standalone') {
    const playable = programme.episodes[0];
    return (
      <Section title="Video" description="A standalone programme has exactly one video.">
        {playable ? (
          <EpisodeTable
            programme={programme}
            episodes={[playable]}
            standalone
            onEdit={(e) => setEpisodeModal({ episode: e })}
          />
        ) : (
          <EmptyState title="No playable yet">
            The API creates it with the programme; reload if it’s missing.
          </EmptyState>
        )}
        {episodeModal && (
          <EpisodeModal
            programme={programme}
            {...episodeModal}
            onClose={() => setEpisodeModal(null)}
            standalone
          />
        )}
      </Section>
    );
  }

  const nextSeason = Math.max(0, ...programme.seasons.map((s) => s.number)) + 1;
  const inSeason = (id: string) => programme.episodes.filter((e) => e.season_id === id);
  const unseasoned = programme.episodes.filter(
    (e) => !e.season_id || !programme.seasons.some((s) => s.id === e.season_id),
  );
  const hasAny = programme.seasons.length > 0 || programme.episodes.length > 0;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Children see episodes in series and episode order. Every non-archived episode needs a
          ready video before approval.
        </Text>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={() => setSeasonModal('new')}
          >
            Add series
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setEpisodeModal({ seasonId: programme.seasons.at(-1)?.id ?? null })}
          >
            Add episode
          </Button>
        </Group>
      </Group>

      {!hasAny && (
        <EmptyState title="No episodes yet">
          Add a series (season) first, then its episodes. Each episode needs a series and an episode
          number to be published.
        </EmptyState>
      )}

      {programme.seasons.map((s) => (
        <Section
          key={s.id}
          title={`Series ${s.number}${s.title ? ` — ${s.title}` : ''}`}
          actions={
            <Group gap={4}>
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconPlus size={14} />}
                onClick={() => setEpisodeModal({ seasonId: s.id })}
              >
                Episode
              </Button>
              <Tooltip label="Edit series">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Edit series ${s.number}`}
                  onClick={() => setSeasonModal(s)}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={inSeason(s.id).length ? 'Only empty series can be deleted' : 'Delete series'}
              >
                <ActionIcon
                  variant="subtle"
                  color="coral"
                  aria-label={`Delete series ${s.number}`}
                  disabled={inSeason(s.id).length > 0}
                  onClick={() =>
                    modals.openConfirmModal({
                      title: `Delete series ${s.number}?`,
                      children: <Text size="sm">It has no episodes, so nothing else changes.</Text>,
                      labels: { confirm: 'Delete', cancel: 'Cancel' },
                      confirmProps: { color: 'coral' },
                      onConfirm: () => deleteSeason.mutate(s.id),
                    })
                  }
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          }
        >
          {inSeason(s.id).length === 0 ? (
            <Text size="sm" c="dimmed">
              No episodes in this series yet.
            </Text>
          ) : (
            <EpisodeTable
              programme={programme}
              episodes={inSeason(s.id)}
              onEdit={(e) => setEpisodeModal({ episode: e })}
            />
          )}
        </Section>
      ))}

      {unseasoned.length > 0 && (
        <Section
          title="Not in a series"
          description="Give these a series and episode number before publishing."
        >
          <EpisodeTable
            programme={programme}
            episodes={unseasoned}
            onEdit={(e) => setEpisodeModal({ episode: e })}
          />
        </Section>
      )}

      {seasonModal && (
        <SeasonModal
          programme={programme}
          season={seasonModal === 'new' ? undefined : seasonModal}
          defaultNumber={nextSeason}
          onClose={() => setSeasonModal(null)}
        />
      )}
      {episodeModal && (
        <EpisodeModal
          programme={programme}
          {...episodeModal}
          onClose={() => setEpisodeModal(null)}
        />
      )}
    </Stack>
  );
}

function EpisodeTable({
  programme,
  episodes,
  standalone,
  onEdit,
}: {
  programme: AdminProgramme;
  episodes: AdminEpisode[];
  standalone?: boolean;
  onEdit: (e: AdminEpisode) => void;
}) {
  const [attachFor, setAttachFor] = useState<AdminEpisode | null>(null);
  const attach = useEpisodeMutation(
    programme.id,
    ({ id, video_id }: { id: string; video_id: string | null }) =>
      adminApi.updateEpisode(id, { video_id }),
  );
  const archive = useEpisodeMutation(
    programme.id,
    (id: string) => adminApi.archiveEpisode(id),
    'Episode archived.',
  );
  const restore = useEpisodeMutation(
    programme.id,
    (id: string) => adminApi.restoreEpisode(id),
    'Episode restored.',
  );

  return (
    <>
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              {!standalone && <Table.Th w={56}>Ep.</Table.Th>}
              <Table.Th>Title</Table.Th>
              <Table.Th>Video</Table.Th>
              <Table.Th w={150} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {episodes.map((e) => (
              <Table.Tr key={e.id} opacity={e.archived ? 0.6 : 1}>
                {!standalone && <Table.Td>{e.episode_number ?? '—'}</Table.Td>}
                <Table.Td>
                  <Text size="sm" fw={500} td={e.archived ? 'line-through' : undefined}>
                    {e.title}
                  </Text>
                  {e.archived && (
                    <Badge size="xs" color="gray" leftSection={<IconArchive size={10} />}>
                      Archived
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {e.video ? (
                    <VideoSummary video={e.video} />
                  ) : (
                    <Text size="sm" c="coral.8">
                      No video
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<IconVideoPlus size={14} />}
                      onClick={() => setAttachFor(e)}
                      disabled={!!e.archived}
                    >
                      {e.video ? 'Replace' : 'Add video'}
                    </Button>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`More actions for ${e.title}`}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(e)}>
                          Edit details
                        </Menu.Item>
                        {e.video && (
                          <Menu.Item
                            leftSection={<IconUnlink size={14} />}
                            onClick={() => attach.mutate({ id: e.id, video_id: null })}
                          >
                            Detach video
                          </Menu.Item>
                        )}
                        {!standalone &&
                          (e.archived ? (
                            <Menu.Item
                              leftSection={<IconRestore size={14} />}
                              onClick={() => restore.mutate(e.id)}
                            >
                              Restore
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              leftSection={<IconArchive size={14} />}
                              color="coral"
                              onClick={() => archive.mutate(e.id)}
                            >
                              Archive
                            </Menu.Item>
                          ))}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {attachFor && (
        <VideoPickerModal
          opened
          onClose={() => setAttachFor(null)}
          purpose="episode"
          title={`Video for “${attachFor.title}”`}
          defaultVideoTitle={
            standalone ? programme.title : `${programme.title} — ${attachFor.title}`
          }
          onPick={async (v: AdminVideo) => {
            await attach.mutateAsync({ id: attachFor.id, video_id: v.id });
            notifications.show({
              color: 'leaf',
              message: `Video attached to “${attachFor.title}”.`,
            });
          }}
        />
      )}
    </>
  );
}

function SeasonModal({
  programme,
  season,
  defaultNumber,
  onClose,
}: {
  programme: AdminProgramme;
  season?: AdminSeason;
  defaultNumber: number;
  onClose: () => void;
}) {
  const form = useForm({
    initialValues: { number: season?.number ?? defaultNumber, title: season?.title ?? '' },
    validate: { number: (n) => (n >= 1 ? null : 'Use 1 or more.') },
  });
  const save = useEpisodeMutation(
    programme.id,
    (v: typeof form.values) =>
      season
        ? adminApi.updateSeason(season.id, { number: v.number, title: v.title.trim() || null })
        : adminApi.createSeason(programme.id, { number: v.number, title: v.title.trim() || null }),
    season ? 'Series updated.' : 'Series added.',
  );
  return (
    <Modal opened onClose={onClose} title={season ? `Edit series ${season.number}` : 'Add series'}>
      <form onSubmit={form.onSubmit((v) => save.mutate(v, { onSuccess: onClose }))}>
        <Stack>
          <NumberInput label="Series number" min={1} required {...form.getInputProps('number')} />
          <TextInput label="Title (optional)" {...form.getInputProps('title')} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function EpisodeModal({
  programme,
  episode,
  seasonId,
  standalone,
  onClose,
}: {
  programme: AdminProgramme;
  episode?: AdminEpisode;
  seasonId?: string | null;
  standalone?: boolean;
  onClose: () => void;
}) {
  const inSeason = (sid: string | null | undefined) =>
    programme.episodes.filter((e) => !!sid && e.season_id === sid);
  const nextNumber = (sid: string | null | undefined) =>
    Math.max(0, ...inSeason(sid).map((e) => e.episode_number ?? 0)) + 1;
  const form = useForm({
    initialValues: {
      title: episode?.title ?? '',
      synopsis: episode?.synopsis ?? '',
      season_id: episode ? episode.season_id : (seasonId ?? null),
      episode_number: (episode ? episode.episode_number : nextNumber(seasonId)) as
        | number
        | string
        | null,
      available_from: toDate(episode?.available_from),
      available_to: toDate(episode?.available_to),
    },
    validate: {
      title: (v) => (v.trim() ? null : 'Add a title.'),
      available_to: (v, all) =>
        v && all.available_from && v <= all.available_from ? 'Must be after the start.' : null,
    },
  });
  const save = useEpisodeMutation(
    programme.id,
    (v: typeof form.values) => {
      const num =
        v.episode_number === '' || v.episode_number == null ? null : Number(v.episode_number);
      const body = standalone
        ? { title: v.title.trim(), synopsis: v.synopsis }
        : {
            title: v.title.trim(),
            synopsis: v.synopsis,
            season_id: v.season_id,
            episode_number: num,
          };
      if (!episode) return adminApi.createEpisode(programme.id, body);
      const window: EpisodePatch = {};
      if (toIso(v.available_from) !== episode.available_from)
        window.available_from = toIso(v.available_from);
      if (toIso(v.available_to) !== episode.available_to)
        window.available_to = toIso(v.available_to);
      return adminApi.updateEpisode(episode.id, { ...body, ...window });
    },
    episode ? 'Episode updated.' : 'Episode added.',
  );
  return (
    <Modal opened onClose={onClose} title={episode ? `Edit “${episode.title}”` : 'Add episode'}>
      <form onSubmit={form.onSubmit((v) => save.mutate(v, { onSuccess: onClose }))}>
        <Stack>
          <TextInput label="Title" required data-autofocus {...form.getInputProps('title')} />
          {!standalone && (
            <Group grow>
              <Select
                label="Series"
                clearable
                data={programme.seasons.map((s) => ({ value: s.id, label: `Series ${s.number}` }))}
                {...form.getInputProps('season_id')}
                onChange={(v) => {
                  form.setFieldValue('season_id', v);
                  if (!episode) form.setFieldValue('episode_number', nextNumber(v));
                }}
              />
              <NumberInput
                label="Episode number"
                min={1}
                {...form.getInputProps('episode_number')}
              />
            </Group>
          )}
          <Textarea label="Synopsis" autosize minRows={3} {...form.getInputProps('synopsis')} />
          {episode && (
            <Group grow>
              <DateTimePicker
                label="Available from"
                description="Optional, narrower than the programme’s window."
                clearable
                valueFormat="D MMM YYYY HH:mm"
                {...form.getInputProps('available_from')}
              />
              <DateTimePicker
                label="Available to"
                clearable
                valueFormat="D MMM YYYY HH:mm"
                {...form.getInputProps('available_to')}
              />
            </Group>
          )}
          {save.error && (
            <Paper p="xs" withBorder>
              <Text size="sm" c="coral.8">
                {errorMessage(save.error)}
              </Text>
            </Paper>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
